import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Chat from "./pages/Chat";
import Layout from "./layouts/Layout";
import EditProfile from "./pages/EditProfile";
import LandingPage from "./pages/LandingPage";
import SearchResults from "./components/SearchResults";
import AppLayout from "./layouts/AppLayout";
import CodeArticleDetail from "./pages/CodeArticleDetail";
import JurisprudenceDetail from "./pages/JurisprudenceDetail";
import JOArticleDetail from "./pages/JOArticleDetail";
import ThesesDetail from "./pages/ThesesDetail";
import LivresDetail from "./pages/LivresDetail";
import TrialContentGuard from "./components/TrialContentGuard";
import SearchLayout from "./layouts/SearchLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import Help from "./pages/Help";
import Dashboard from "./pages/Dashboard";
import DashboardFavorites from "./pages/DashboardFavorites";
import DashboardSubscription from "./pages/DashboardSubscription";
import Pricing from "./pages/Pricing";
import LanguageWrapper from "./LanguageWrapper";
import LegacyRouteHandler from "./LegacyRouteHandler";
import AutoLogin from "./pages/AutoLogin";
import PaymentResult from "./pages/PaymentResult";
import PdfViewer from "./pages/PdfViewer";
import PdfViewerNative from "./pages/PdfViewerNative";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { useTranslation } from "react-i18next";
import ScrollToTop from "./components/ScrollToTop";
import VersionChecker from "./components/VersionChecker";

function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const { i18n } = useTranslation();

  // Sync token state with localStorage (for logout from other components)
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== token) {
        setToken(currentToken);
      }
    };

    // Check periodically for token changes
    const interval = setInterval(checkToken, 500);

    // Also listen for storage events (from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token") {
        setToken(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [token]);

  const handleLogout = () => {
    // Notify the API to free the session slot
    const currentToken = localStorage.getItem("token");
    if (currentToken) {
      const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';
      // keepalive ensures the request completes even if the page is unloading
      fetch(`${backendUrl}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${currentToken}` },
        keepalive: true
      }).catch(() => {});
    }
    setToken(null);
    localStorage.removeItem("token");
  };

  return (
    <Router>
      <ScrollToTop />
      <VersionChecker />
      <Routes>
        {/* Root redirect to default language */}
        <Route path="/" element={<Navigate to={`/${i18n.language}`} replace />} />

        {/* Language Wrapper Route */}
        <Route path="/:lang" element={<LanguageWrapper />}>

          {/* Public routes wrapped in AppLayout */}
          <Route element={<AppLayout />}>
            <Route index element={<LandingPage />} />

            {/* Search Layout for Search and Details - Protected */}
            <Route
              element={
                token ? (
                  <SearchLayout />
                ) : (
                  <Navigate to="login" replace />
                )
              }
            >
              <Route path="search" element={<SearchResults />} />
              <Route path="detail/code-article/:id" element={<CodeArticleDetail />} />
              <Route path="detail/jurisprudence/:id" element={<JurisprudenceDetail />} />
              <Route path="detail/jo/:id" element={<JOArticleDetail />} />
              <Route path="detail/theses/:id" element={<TrialContentGuard><ThesesDetail /></TrialContentGuard>} />
              <Route path="detail/livres/:id" element={<TrialContentGuard><LivresDetail /></TrialContentGuard>} />
            </Route>
            <Route path="help" element={<Help />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="pdf/:type/:id" element={<PdfViewer />} />
            <Route path="pdf-native/:type/:id" element={<PdfViewerNative />} />

            {/* Dashboard Layout with sidebar navigation */}
            <Route
              element={
                token ? (
                  <DashboardLayout onLogout={handleLogout} />
                ) : (
                  <Navigate to="login" replace />
                )
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="dashboard/favorites" element={<DashboardFavorites />} />
              <Route path="dashboard/subscription" element={<DashboardSubscription />} />
              <Route path="dashboard/profile" element={<EditProfile />} />
            </Route>
          </Route>

          <Route
            path="login"
            element={
              token ? (
                <Navigate to="chat" replace />
              ) : (
                <Login
                  setToken={(t) => {
                    setToken(t);
                    localStorage.setItem("token", t);
                  }}
                />
              )
            }
          />

          <Route
            path="register"
            element={token ? <Navigate to="chat" replace /> : <Register />}
          />


          {/* Payment result (Chargily redirect) — inside AppLayout */}
          <Route element={<AppLayout />}>
            <Route path="payment/result" element={
              <PaymentResult setToken={(t) => {
                setToken(t);
                localStorage.setItem("token", t);
              }} />
            } />
          </Route>

          {/* Auto-login from payment confirmation email */}
          <Route
            path="auto-login"
            element={
              <AutoLogin
                setToken={(t) => {
                  setToken(t);
                  localStorage.setItem("token", t);
                }}
              />
            }
          />

          {/* Forgot Password */}
          <Route
            path="forgot-password"
            element={token ? <Navigate to="dashboard/profile" replace /> : <ForgotPassword />}
          />

          {/* Reset Password */}
          <Route
            path="reset-password"
            element={<ResetPassword />}
          />

          {/* Protected routes inside Layout */}
          <Route
            element={
              <Layout onLogout={handleLogout} />
            }
          >
            <Route
              path="chat"
              element={token ? <Chat /> : <Navigate to="login" replace />}
            />

            {/* ✅ Profile page now uses the SAME layout */}
            <Route
              path="profile"
              element={token ? <EditProfile /> : <Navigate to="login" replace />}
            />
          </Route>

          {/* Catch all within language scope */}
          <Route path="*" element={<Navigate to="/" />} />
        </Route>

        {/* Catch all global - Redirect to same path with default language */}
        <Route path="*" element={<LegacyRouteHandler />} />
      </Routes>
    </Router>
  );
}

export default App;

